/**
 * Device Parser Utility
 * Parses user-agent strings to extract meaningful device information
 */

export interface DeviceInfo {
  type:
    | "iPhone"
    | "iPad"
    | "Android Phone"
    | "Android Tablet"
    | "Windows PC"
    | "Mac"
    | "Linux PC"
    | "Unknown";
  os: "iOS" | "Android" | "Windows" | "macOS" | "Linux" | "Unknown";
  browser: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

/**
 * Parse user-agent string to extract device information
 */
export function parseUserAgent(userAgent: string): DeviceInfo {
  const ua = userAgent.toLowerCase();

  // Initialize result
  const result: DeviceInfo = {
    type: "Unknown",
    os: "Unknown",
    browser: getBrowser(ua),
    isMobile: false,
    isTablet: false,
    isDesktop: false,
  };

  // Detect iOS devices
  if (ua.includes("iphone")) {
    result.type = "iPhone";
    result.os = "iOS";
    result.isMobile = true;
  } else if (ua.includes("ipad")) {
    result.type = "iPad";
    result.os = "iOS";
    result.isTablet = true;
  }
  // Detect Android devices
  else if (ua.includes("android")) {
    result.os = "Android";
    // Check if it's a tablet (no 'mobile' keyword or specific tablet indicators)
    if (!ua.includes("mobile") || ua.includes("tablet") || isLikelyTablet(ua)) {
      result.type = "Android Tablet";
      result.isTablet = true;
    } else {
      result.type = "Android Phone";
      result.isMobile = true;
    }
  }
  // Detect Windows
  else if (ua.includes("windows")) {
    result.type = "Windows PC";
    result.os = "Windows";
    result.isDesktop = true;
  }
  // Detect macOS
  else if (ua.includes("macintosh") || ua.includes("mac os")) {
    result.type = "Mac";
    result.os = "macOS";
    result.isDesktop = true;
  }
  // Detect Linux
  else if (ua.includes("linux") && !ua.includes("android")) {
    result.type = "Linux PC";
    result.os = "Linux";
    result.isDesktop = true;
  }

  return result;
}

/**
 * Get browser name from user-agent
 */
function getBrowser(ua: string): string {
  if (ua.includes("edg/")) return "Edge";
  if (ua.includes("chrome") && !ua.includes("edg")) return "Chrome";
  if (ua.includes("safari") && !ua.includes("chrome")) return "Safari";
  if (ua.includes("firefox")) return "Firefox";
  if (ua.includes("opera") || ua.includes("opr/")) return "Opera";
  if (ua.includes("trident/") || ua.includes("msie")) return "IE";
  return "Unknown";
}

/**
 * Check if Android device is likely a tablet based on screen size hints
 */
function isLikelyTablet(ua: string): boolean {
  // Common tablet indicators
  const tabletIndicators = [
    "sm-t", // Samsung tablets
    "gt-p", // Samsung Galaxy Tab
    "sm-p", // Samsung Galaxy Note tablets
    "nexus 7",
    "nexus 9",
    "nexus 10",
    "xoom",
    "kindle",
    "silk", // Amazon Silk browser
    "kfapwi", // Kindle Fire
  ];

  return tabletIndicators.some((indicator) => ua.includes(indicator));
}

/**
 * Get simplified device string for logging
 */
export function getSimplifiedDeviceString(userAgent: string): string {
  const device = parseUserAgent(userAgent);

  // Return the device type with browser
  if (device.browser !== "Unknown") {
    return `${device.type} (${device.browser})`;
  }
  return device.type;
}

/**
 * Get device category for analytics
 */
export function getDeviceCategory(
  userAgent: string,
): "mobile" | "tablet" | "desktop" | "unknown" {
  const device = parseUserAgent(userAgent);

  if (device.isMobile) return "mobile";
  if (device.isTablet) return "tablet";
  if (device.isDesktop) return "desktop";
  return "unknown";
}
